import React, { useState, useEffect } from "react";
import { API } from "../lib/appCommon";
import { fastGet } from "../lib/fastFetch";

// ─── Omega AI Picks Panel ────────────────────────────────────────────────────
export const OMEGA_PICKS_LIST = [
  { symbol: "NVDA", reason: "AI infrastructure leader, IV elevated pre-earnings" },
  { symbol: "AAPL", reason: "High options liquidity, tight bid-ask, steady momentum" },
  { symbol: "TSLA", reason: "High volatility — strong gamma opportunities both sides" },
  { symbol: "META", reason: "Bullish momentum, options flow heavily bullish skew" },
  { symbol: "AMD",  reason: "Semiconductor cycle plays, correlated with NVDA" },
  { symbol: "AMZN", reason: "Cloud + AI narrative, options chain very liquid" },
  { symbol: "PLTR", reason: "AI defense growth, elevated IV on macro events" },
  { symbol: "SPY",  reason: "Market hedge and directional plays, tightest spreads" },
  { symbol: "QQQ",  reason: "Tech index, useful for sector-level options positioning" },
  { symbol: "HOOD", reason: "High retail options flow, binary earnings setups" },
  { symbol: "MSTR", reason: "Bitcoin proxy with extreme IV — high-gamma plays" },
  { symbol: "COIN", reason: "Crypto-correlated, elevated IV around BTC moves" },
];

function OmegaAIPicksPanel({ onPickClick, onRunScan }) {
  const [quotes, setQuotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [scanningPick, setScanningPick] = useState(null);

  useEffect(() => {
    const syms = OMEGA_PICKS_LIST.map(p => p.symbol).join(",");
    const stop = fastGet(`${API}/market/quotes?symbols=${syms}`, (data) => {
      const q = {};
      (Array.isArray(data) ? data : []).forEach(qt => {
        if (!qt?.symbol) return;
        q[qt.symbol] = {
          price:     qt.price ?? qt.last_price ?? null,
          prevClose: qt.prevClose ?? null,
          name:      qt.name || qt.symbol,
        };
      });
      setQuotes(q);
      setLoading(false);
    });
    return stop;
  }, []);

  const runScan = async (symbol) => {
    setScanningPick(symbol);
    try { await onRunScan([symbol]); } finally { setScanningPick(null); }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "var(--text)" }}>✦ Omega AI Picks</div>
          <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>High-conviction watchlist · preloaded · instant open</div>
        </div>
        <button className="btn btn-blue" style={{ fontSize: 10 }}
          onClick={() => onRunScan(OMEGA_PICKS_LIST.map(p => p.symbol))}
        >
          Run Scan on All Picks
        </button>
      </div>

      {/* Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
        {OMEGA_PICKS_LIST.map(pick => {
          const q = quotes[pick.symbol];
          const price = q?.price;
          const change = price != null && q?.prevClose != null ? price - q.prevClose : null;
          const changePct = q?.prevClose && change != null ? (change / q.prevClose) * 100 : null;
          const isUp = change == null ? null : change >= 0;
          const priceColor = isUp == null ? "var(--muted)" : isUp ? "#4ade80" : "#f87171";
          const isScanning = scanningPick === pick.symbol;

          return (
            <div key={pick.symbol}
              style={{
                background: "var(--layer2)",
                border: `1px solid ${isUp == null ? "var(--border)" : isUp ? "rgba(74,222,128,0.25)" : "rgba(248,113,113,0.25)"}`,
                borderRadius: 14, padding: "14px 16px", cursor: "pointer",
                transition: "transform 0.12s, box-shadow 0.12s",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 24px ${priceColor}22`; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
            >
              {/* Symbol + price row */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 900, color: "var(--text)" }}>{pick.symbol}</div>
                  {loading ? (
                    <div style={{ fontSize: 11, color: "var(--dim)", marginTop: 2 }}>Loading…</div>
                  ) : price != null ? (
                    <div style={{ marginTop: 2 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: priceColor }}>${price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      {changePct != null && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: priceColor, marginLeft: 6 }}>
                          {changePct > 0 ? "+" : ""}{changePct.toFixed(2)}%
                        </span>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: "var(--dim)", marginTop: 2 }}>—</div>
                  )}
                </div>
                {/* Direction pill */}
                {isUp != null && (
                  <div style={{ fontSize: 9, fontWeight: 800, padding: "3px 8px", borderRadius: 20,
                    background: isUp ? "rgba(74,222,128,0.12)" : "rgba(248,113,113,0.12)",
                    color: isUp ? "#4ade80" : "#f87171",
                    border: `1px solid ${isUp ? "rgba(74,222,128,0.25)" : "rgba(248,113,113,0.25)"}`,
                  }}>
                    {isUp ? "▲" : "▼"}
                  </div>
                )}
              </div>

              {/* Reason */}
              <div style={{ fontSize: 10, color: "var(--dim)", lineHeight: 1.5, marginBottom: 10 }}>{pick.reason}</div>

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn" style={{ fontSize: 9, padding: "4px 10px", flex: 1 }}
                  onClick={() => onPickClick(pick.symbol)}>
                  Analyze
                </button>
                <button className="btn btn-blue" style={{ fontSize: 9, padding: "4px 10px", flex: 1 }}
                  disabled={isScanning}
                  onClick={e => { e.stopPropagation(); runScan(pick.symbol); }}>
                  {isScanning ? "Scanning…" : "Zi Scan"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default OmegaAIPicksPanel;
